import { ReactNode } from "react";
import { useTheme } from "@emotion/react";
import {
  createTheme,
  ThemeProvider,
  PaletteColor,
  PaletteColorOptions,
} from "@mui/material";

declare module "@mui/material/styles" {
  interface Palette {
    tertiary: PaletteColor;
    brand: PaletteColor;
  }
  interface PaletteOptions {
    tertiary: PaletteColorOptions;
    brand?: PaletteColorOptions;
  }
}

const commonStyles = {
  fontFamily: "var(--font-family)",
  body1FontSize: "var(--body1-font-size)",
  body1LineHeight: "var(--body1-line-height)",
  body1FontWeight: "var(--body1-font-weight)",

  body2FontSize: "var(--body2-font-size)",
  body2LineHeight: "var(--body2-line-height)",
  body2FontWeight: "var(--body2-font-weight)",

  header1FontSize: "var(--header1-font-size)",
  header1LineHeight: "var(--header1-line-height)",
  header1FontWeight: "var(--header1-font-weight)",

  header2FontSize: "var(--header2-font-size)",
  header2LineHeight: "var(--header2-line-height)",
  header2FontWeight: "var(--header2-font-weight)",

  header3FontSize: "var(--header3-font-size)",
  header3LineHeight: "var(--header3-line-height)",
  header3FontWeight: "var(--header3-font-weight)",

  header4FontSize: "var(--header4-font-size)",
  header4LineHeight: "var(--header4-line-height)",
  header4FontWeight: "var(--header4-font-weight)",

  header5FontSize: "var(--header5-font-size)",
  header5LineHeight: "var(--header5-line-height)",
  header5FontWeight: "var(--header5-font-weight)",

  subtitle1FontSize: "var(--subtitle1-font-size)",
  subtitle1LineHeight: "var(--subtitle1-line-height)",
  subtitle1FontWeight: "var(--subtitle1-font-weight)",

  bodyBackgroundColor: "var(--body-background-color)",
  // todo: type mismatch issue
  // todo: if variables from global passed here, not working in mui theme
  primaryTextColor: "#222222", // body text
  brandColor: "#bd00cd",
  brandLightColor: "#bd00cd2e",
  primaryColor: "#adadad", // primary, background, dark, etc
  primaryLightColor: "#8C8C8C",
  primaryDarkColor: "#717171",
  secondaryColor: "#dfdfdf", // border color
  secondaryDarkColor: "#cecece",
  tertiaryColor: "#dfdfdf", // border
  tertiaryDarkColor: "#cecece", // border dark
};

export const MuiThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();

  const muiTheme = createTheme({
    ...theme,
    breakpoints: {
      values: {
        xs: 0, // Extra small devices (phones)
        sm: 601, // Small devices (landscape phones)
        md: 961, // Medium devices (tablets)
        lg: 1441, // Large devices (desktops)
        xl: 1921, // Extra large devices (large desktops)
      },
    },
    typography: {
      fontFamily: commonStyles.fontFamily,
      htmlFontSize: 14,
      fontSize: 14,
      allVariants: {
        color: commonStyles.primaryTextColor,
      },
    },
    palette: {
      primary: {
        main: commonStyles.primaryColor,
        light: commonStyles.primaryLightColor,
        dark: commonStyles.primaryDarkColor,
      },
      secondary: {
        main: commonStyles.secondaryColor,
        dark: commonStyles.secondaryDarkColor,
      },
      tertiary: {
        main: commonStyles.tertiaryColor,
        dark: commonStyles.tertiaryDarkColor,
      },
      brand: {
        main: commonStyles.brandColor,
        light: commonStyles.brandLightColor,
      },
      text: {
        primary: commonStyles.primaryTextColor,
      },
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: () => {
            return {
              textTransform: "none",
              // margin: "0 30px",
            };
          },
        },
      },
      // MuiAccordion: {
      //   styleOverrides: {
      //     root: () => {
      //       return {
      //         "& .MuiAccordionSummary-root.Mui-expanded": {
      //           minHeight: "48px",
      //         },
      //         "& .MuiAccordionSummary-content.Mui-expanded": {
      //           margin: "12px 0",
      //         },
      //       };
      //     },
      //   },
      // },
      // todo: revise this
      // todo: include global search css for border, hover, selected css here
      MuiButton: {
        variants: [
          // {
          //   props: { variant: "contained" },
          //   style: {
          //     color: "#FFFFFF",
          //     background: `linear-gradient(103deg, ${commonStyles.primaryColor} 12%, #32376f 100%)`,
          //     "&:hover": {
          //       background: `linear-gradient(103deg, ${commonStyles.primaryColor} 12%, #7a008d 100%)`,
          //     },
          //   },
          // },
          // {
          //   props: { variant: "outlined" },
          //   style: {
          //     borderColor: commonStyles.tertiaryColor,
          //     color: commonStyles.primaryTextColor,
          //     "&:hover": {
          //       background: "#ffffff",
          //       borderColor: commonStyles.tertiaryDarkColor,
          //       color: commonStyles.primaryTextColor,
          //     },
          //   },
          // },
          {
            props: { disabled: true },
            style: {
              color: "#FFFFFF !important",
            },
          },
        ],
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: commonStyles.tertiaryColor,
            // todo: add hover and focused css here for select from global and calendar
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: 'var(--font-family)',
          },
        },
      },
    },
  });

  muiTheme.typography.h1 = {
    fontSize: commonStyles.header1FontSize, //18px
    lineHeight: commonStyles.header1LineHeight,
    fontWeight: commonStyles.header1FontWeight,
    [muiTheme.breakpoints.down("lg")]: {
      fontSize: "16px",
    },
  };

  muiTheme.typography.h2 = {
    fontSize: commonStyles.header2FontSize, //16px
    lineHeight: commonStyles.header2LineHeight,
    fontWeight: commonStyles.header2FontWeight,
    [muiTheme.breakpoints.down("lg")]: {
      fontSize: "15px",
    },
  };

  muiTheme.typography.h3 = {
    fontSize: commonStyles.header3FontSize, //15px
    lineHeight: commonStyles.header3LineHeight,
    fontWeight: commonStyles.header3FontWeight,
    [muiTheme.breakpoints.down("lg")]: {
      fontSize: "14px",
    },
  };

  muiTheme.typography.h4 = {
    fontSize: commonStyles.header4FontSize, //16px
    lineHeight: commonStyles.header4LineHeight,
    fontWeight: commonStyles.header4FontWeight,
    [muiTheme.breakpoints.down("lg")]: {
      fontSize: "14px",
    },
  };

  muiTheme.typography.h5 = {
    fontSize: commonStyles.header5FontSize, //14px
    lineHeight: commonStyles.header5LineHeight,
    fontWeight: commonStyles.header5FontWeight,
    [muiTheme.breakpoints.down("lg")]: {
      fontSize: "12px",
    },
  };

  muiTheme.typography.body1 = {
    fontSize: commonStyles.body1FontSize, //14px
    lineHeight: commonStyles.body1LineHeight,
    fontWeight: commonStyles.body1FontWeight, //21px
    // [muiTheme.breakpoints.down("lg")]: {
    //   fontSize: "12px",
    //   fontWeight: "18px",
    // },
  };

  muiTheme.typography.body2 = {
    fontSize: commonStyles.body2FontSize, //12px
    lineHeight: commonStyles.body2LineHeight,
    fontWeight: commonStyles.body2FontWeight, //21px
  };

  muiTheme.typography.subtitle1 = {
    fontSize: commonStyles.subtitle1FontSize, //12px
    lineHeight: commonStyles.subtitle1FontSize,
    fontWeight: commonStyles.subtitle1FontWeight, //18px
    // [muiTheme.breakpoints.down("lg")]: {
    //   fontSize: "10px",
    //   fontWeight: "14px",
    // },
  };

  return <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>;
};
